import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components/macro";
import { useSelector } from "react-redux";
import { Helmet } from "react-helmet-async";
import { useSnackbar } from "notistack";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Loader from "../../components/Loader";
import api from "../../api/api";
import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button as MuiButton,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider as MuiDivider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField as MuiTextField,
  Typography,
} from "@material-ui/core";
import { spacing } from "@material-ui/system";

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Divider = styled(MuiDivider)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);
const QuillWrapperTop = styled.div`
  .ql-editor {
    min-height: 200px;
  }
`;

function ManageEmailTemplates() {
  const auth = useSelector((state) => state.authReducer);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(0);
  const [savingTemplateIds, setSavingTemplateIds] = useState({});

  useEffect(() => {
    setIsLoading(true);
    setError(null);

    api
      .get("api/EmailTemplate/GetTemplateGroups?v=1.0", {
        headers: {
          Authorization: `Bearer ${auth.user?.token}`,
        },
      })
      .then((response) => {
        if (response?.data?.isSuccess) {
          setGroups(response?.data?.data || []);
          return;
        }

        setError(
          response?.data?.message ||
            response?.data?.errorMessage ||
            "Failed to load email templates."
        );
      })
      .catch((requestError) => {
        setError(requestError?.message || "Failed to load email templates.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [auth.user?.token]);

  const hasTemplates = useMemo(
    () =>
      groups.some(
        (group) => Array.isArray(group?.templates) && group.templates.length > 0
      ),
    [groups]
  );

  const flattenedTemplates = useMemo(
    () =>
      groups.flatMap((group, groupIndex) =>
        (group.templates || []).map((template, templateIndex) => ({
          ...template,
          area: group.area,
          groupIndex,
          templateIndex,
          optionLabel: `[${group.area}] ${template.name}`,
        }))
      ),
    [groups]
  );

  useEffect(() => {
    if (!flattenedTemplates.length) {
      setSelectedTemplateId(0);
      return;
    }

    const hasSelectedTemplate = flattenedTemplates.some(
      (template) => template.id === Number(selectedTemplateId)
    );

    if (!hasSelectedTemplate) {
      setSelectedTemplateId(flattenedTemplates[0].id);
    }
  }, [flattenedTemplates, selectedTemplateId]);

  const selectedTemplate = useMemo(
    () =>
      flattenedTemplates.find(
        (template) => template.id === Number(selectedTemplateId)
      ) || null,
    [flattenedTemplates, selectedTemplateId]
  );

  const handleFieldChange = (groupIndex, templateIndex, field, value) => {
    setGroups((previousGroups) =>
      previousGroups.map((group, currentGroupIndex) => {
        if (currentGroupIndex !== groupIndex) {
          return group;
        }

        return {
          ...group,
          templates: (group.templates || []).map(
            (template, currentTemplateIdx) =>
              currentTemplateIdx === templateIndex
                ? {
                    ...template,
                    [field]: value,
                  }
                : template
          ),
        };
      })
    );
  };

  const handleSaveTemplate = (template) => {
    setSavingTemplateIds((previous) => ({
      ...previous,
      [template.id]: true,
    }));

    api
      .put(
        "api/EmailTemplate/UpdateTemplate?v=1.0",
        {
          id: template.id,
          subject: template.subject || "",
          title: template.title || "",
          body: template.body || "",
          isActive: !!template.isActive,
        },
        {
          headers: {
            Authorization: `Bearer ${auth.user?.token}`,
          },
        }
      )
      .then((response) => {
        if (response?.data?.isSuccess) {
          enqueueSnackbar("Email template updated successfully.", {
            variant: "success",
            autoHideDuration: 3000,
          });
          return;
        }

        enqueueSnackbar(
          response?.data?.message ||
            response?.data?.errorMessage ||
            "Failed to update email template.",
          {
            variant: "error",
            autoHideDuration: 4000,
          }
        );
      })
      .catch((requestError) => {
        enqueueSnackbar(
          requestError?.message || "Failed to update email template.",
          {
            variant: "error",
            autoHideDuration: 4000,
          }
        );
      })
      .finally(() => {
        setSavingTemplateIds((previous) => ({
          ...previous,
          [template.id]: false,
        }));
      });
  };

  if (isLoading) return <Loader />;
  if (error) return <div style={{ color: "red" }}>{error}</div>;

  return (
    <React.Fragment>
      <Helmet title="Manage Email Templates" />

      <Grid justify="space-between" container spacing={4}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Manage Email Templates
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Typography>Pages</Typography>
            <Typography>Manage Email Templates</Typography>
          </Breadcrumbs>
        </Grid>
      </Grid>

      <Divider my={6} />

      {!hasTemplates ? (
        <Typography variant="body1">No templates found.</Typography>
      ) : (
        <Card style={{ marginBottom: 24 }}>
          <CardContent>
            <Typography variant="h4" gutterBottom>
              Email Templates
            </Typography>

            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel id="email-template-select-label">Template</InputLabel>
              <Select
                labelId="email-template-select-label"
                value={selectedTemplateId}
                onChange={(event) =>
                  setSelectedTemplateId(Number(event.target.value))
                }
                label="Template"
              >
                {flattenedTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.optionLabel}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedTemplate && (
              <Box mt={3}>
                <Typography variant="h6">{selectedTemplate.name}</Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Area: {selectedTemplate.area}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {selectedTemplate.templateKey}
                </Typography>

                <TextField
                  fullWidth
                  label="Subject"
                  variant="outlined"
                  value={selectedTemplate.subject || ""}
                  onChange={(event) =>
                    handleFieldChange(
                      selectedTemplate.groupIndex,
                      selectedTemplate.templateIndex,
                      "subject",
                      event.target.value
                    )
                  }
                  my={2}
                />
                <TextField
                  fullWidth
                  label="Title"
                  variant="outlined"
                  value={selectedTemplate.title || ""}
                  onChange={(event) =>
                    handleFieldChange(
                      selectedTemplate.groupIndex,
                      selectedTemplate.templateIndex,
                      "title",
                      event.target.value
                    )
                  }
                  my={2}
                />
                <Box my={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Body
                  </Typography>
                  <QuillWrapperTop>
                    <ReactQuill
                      key={selectedTemplate.id}
                      theme="snow"
                      value={selectedTemplate.body || ""}
                      onChange={(value) =>
                        handleFieldChange(
                          selectedTemplate.groupIndex,
                          selectedTemplate.templateIndex,
                          "body",
                          value
                        )
                      }
                      placeholder="Body"
                    />
                  </QuillWrapperTop>
                </Box>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={!!selectedTemplate.isActive}
                        onChange={(event) =>
                          handleFieldChange(
                            selectedTemplate.groupIndex,
                            selectedTemplate.templateIndex,
                            "isActive",
                            event.target.checked
                          )
                        }
                      />
                    }
                    label="Active"
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => handleSaveTemplate(selectedTemplate)}
                    disabled={!!savingTemplateIds[selectedTemplate.id]}
                  >
                    {savingTemplateIds[selectedTemplate.id] ? (
                      <CircularProgress size={18} style={{ color: "white" }} />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </React.Fragment>
  );
}

export default ManageEmailTemplates;
